import { Cell, flexRender, Row, Table } from "@tanstack/react-table";
import { Body, BodyColor, BodyType, Heading, TypographyColor, TypographyType } from "..";

interface VirtuosoTableRowProps<T extends object> {
	rows: any;
	rowStyle?: React.CSSProperties | ((row: Row<T>) => React.CSSProperties);
	cellStyle?: React.CSSProperties | ((cell: Cell<T, unknown>) => React.CSSProperties);
	primaryKey?: string;
	tableConfig?: any;
	selIndex?: number;
	onRowClick?: (row: any) => void;
	[key: string]: any;
}

export const VirtuosoTableRow = <T extends object>({
	rows,
	rowStyle,
	cellStyle,
	tableConfig,
	selIndex,
	onRowClick,
	...props
}: VirtuosoTableRowProps<T>) => {
	const rowIndex  = props["data-index"];
	const row = rows[rowIndex];
	const appliedRowStyle = typeof rowStyle === "function" ? rowStyle(row) : rowStyle;
	const rowBackgroundColor = appliedRowStyle?.backgroundColor;

	return (
		<tr
			{...props}
			style={{
				...appliedRowStyle,
			}}
		>
			{row.getVisibleCells().map((cell: any, index: number) => {
				const appliedCellStyle = typeof cellStyle === "function" ? cellStyle(cell) : cellStyle;

				const isPrimary = tableConfig?.PRIMARY_KEY && cell.column.id === tableConfig?.PRIMARY_KEY;
				const stickyIndices = tableConfig?.PINNED_ROW_INDICES ?? [];
				const isPinned = stickyIndices.includes(index);

				const cellValue = cell.getValue();
				const plainText = typeof cellValue === "string" ? cellValue : (cellValue as any)?.toString?.() ?? "";
				const showTitle = plainText.length > 15 ? plainText : undefined;

				const baseWidth = cell.column.getSize();
				const adjustedWidth = isPrimary ? baseWidth * 2 : plainText.includes('http') ? baseWidth * 1.29 : baseWidth;

				return (
					<td
						key={cell.id}
						className={`text-truncate text-start padding-left-right-5 align-middle ${isPinned ? 'position-sticky z-index-1' : ''} ${cell.column.id === 'actions' || cell.column.id === 'geoName' ? 'position-relative' : ''}`}
						title={showTitle}
						style={{
							...appliedCellStyle,
							backgroundColor: selIndex !== undefined && selIndex === rowIndex ? "#AEC2FF" : (rowBackgroundColor || "white"), // Highlight if selected
							width: adjustedWidth,
							maxWidth: adjustedWidth,
							minWidth: adjustedWidth,
							left: isPinned ? `${cell.column.getStart('left')}px` : undefined,
							boxShadow: isPinned ? `1px 0px 3px rgba(0, 0, 0, 0.1)` : '',
						}}
					>
						{isPrimary ? (
							<Heading
								title={flexRender(cell.column.columnDef.cell, cell.getContext())}
								colour={TypographyColor.primary}
								type={TypographyType.h5}
								classname='text-truncate d-block p-0 m-0 padding-top-bottom-2'
								onClick={() => onRowClick?.(row)}
							/>
						) : (
							<Body
								type={BodyType.p2}
								color={BodyColor.secondary}
								classname="text-truncate d-block padding-top-bottom-2"
							>
								{flexRender(cell.column.columnDef.cell, cell.getContext())}
							</Body>
						)}
					</td>
				);
			})}
		</tr>
	);
};
